import * as Yup from 'yup'

import {
  AccommodationType,
  ContractType,
  CreatePostSchema,
  Gender,
  PostType,
  ProfileType,
  RoomType
} from './types'

const emptyStringToNull = (value: number | null, originalValue: unknown) =>
  typeof originalValue === 'string' && originalValue.trim() === '' ? null : value

export const postValidationSchema = Yup.object({
  user_id: Yup.number().transform(emptyStringToNull).nullable(),
  post_type: Yup.number().transform(emptyStringToNull).nullable().required('Campo obbligatorio'),
  description: Yup.object({
    it: Yup.string().required('Campo obbligatorio'),
    en: Yup.string()
  }),
  price: Yup.number().transform(emptyStringToNull).min(0).required('Prezzo obbligatorio'),
  available_from: Yup.string()
    .required('Campo obbligatorio')
    .test(
      'available-from-not-past',
      'La data di disponibilità non può essere nel passato',
      function (value) {
        if (!value) return true
        const today = new Date().toISOString().slice(0, 10)
        return value >= today
      }
    ),
  available_to: Yup.string()
    .nullable()
    .required('Campo obbligatorio')
    .test(
      'available-to-after-or-equal',
      'La data "Disponibile fino a" deve essere uguale o successiva a "Disponibile da"',
      function (value) {
        if (!value) return true

        const { available_from } = this.parent as { available_from?: string | null }
        if (!available_from) return true

        return value >= available_from
      }
    ),
  city_id: Yup.number().transform(emptyStringToNull).nullable().required('Campo obbligatorio'),
  gender: Yup.number().transform(emptyStringToNull).nullable().required('Campo obbligatorio'),
  profile_type: Yup.number().transform(emptyStringToNull).nullable().required('Campo obbligatorio'),
  accommodation_type: Yup.number()
    .transform(emptyStringToNull)
    .nullable()
    .required('Campo obbligatorio'),
  room_type: Yup.number().transform(emptyStringToNull).nullable().required('Campo obbligatorio'),
  contract_type: Yup.number()
    .transform(emptyStringToNull)
    .nullable()
    .when('post_type', {
      is: 0,
      then: (schema) => schema.oneOf([0, 1, null]),
      otherwise: (schema) => schema.oneOf([null], 'Campo valido solo per post di tipo Offerta')
    }),
  active: Yup.boolean().required('Campo obbligatorio'),
  approved: Yup.boolean().nullable(),
  included_services: Yup.array().of(Yup.number())
})

export const postInitialValues = {
  user_id: null as number | null,
  post_type: null as PostType | null,
  description: { it: '', en: '' },
  active: true,
  approved: null as boolean | null,
  price: null as string | null,
  available_from: null as string | null,
  available_to: null as string | null,
  city_id: null as number | null,
  gender: null as Gender | null,
  profile_type: null as ProfileType | null,
  accommodation_type: null as AccommodationType | null,
  room_type: null as RoomType | null,
  contract_type: null as ContractType | null,
  included_services: [] as number[]
}

export type PostFormValues = typeof postInitialValues

type PostFormPayload = Omit<Partial<PostFormValues>, 'user_id' | 'price'> & {
  user_id?: number | string | null
  price?: number | string | null
}

type BuildPostPayloadOptions = {
  fallbackUserId?: number | null
  canEditPosts?: boolean
  useFallbackUserId?: boolean
}

export const buildPostPayload = (
  values: PostFormPayload,
  options: BuildPostPayloadOptions = {}
) => {
  const { user_id, active, approved, ...rest } = values
  const normalizedPrice = (() => {
    if (rest.price === undefined) return undefined
    if (rest.price === null) return null

    if (typeof rest.price === 'number') {
      return Number.isFinite(rest.price) ? String(rest.price) : null
    }

    if (typeof rest.price === 'string') {
      const trimmedValue = rest.price.trim()
      if (!trimmedValue) return null

      const numericValue = Number(trimmedValue)
      return Number.isFinite(numericValue) ? trimmedValue : null
    }

    return undefined
  })()

  const payload = {
    ...rest,
    post_type: rest.post_type,
    contract_type: rest.post_type === 0 ? rest.contract_type : null,
    price: normalizedPrice,
    active,
    approved
  }
  const parsedUserId = (() => {
    if (!options.canEditPosts) {
      return null
    }

    if (typeof user_id === 'number') return user_id
    if (typeof user_id === 'string') {
      const trimmedValue = user_id.trim()
      if (!trimmedValue) return null

      const numericValue = Number(trimmedValue)
      return Number.isFinite(numericValue) ? numericValue : null
    }

    return null
  })()

  const finalUserId =
    typeof parsedUserId === 'number'
      ? parsedUserId
      : options.useFallbackUserId === false
        ? null
        : options.fallbackUserId

  if (typeof finalUserId === 'number') {
    return { ...payload, user_id: finalUserId } satisfies Partial<CreatePostSchema>
  }

  return payload satisfies Partial<CreatePostSchema>
}
